import { Text } from '@react-spectrum/s2';
import Close from '@react-spectrum/s2/icons/Close';
import {
	iconStyle,
	style,
} from '@react-spectrum/s2/style' with { type: 'macro' };
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button as RACButton } from 'react-aria-components';
import { createPortal } from 'react-dom';

import { useBridgeApi } from '@/api/BridgeApi';
import { AISparklesIcon } from '@/components/icons/AISparklesIcon';
import { useBusinessName } from '@/hooks/useBusinessName';
import { useExitAnimation } from '@/hooks/useExitAnimation';
import {
	PILLS,
	type PillOption,
	type PillSpec,
} from '@/spaces/chat/chatEmptyStateData';
import { useRegisterUserInputStartHandler } from '@/spaces/chat/ChatInputSeedContext';
import { ForYouTaskList } from '@/spaces/chat/ForYouTaskList';
import { PillChip } from '@/spaces/chat/PillChip';
import { pillTransitionName } from '@/spaces/chat/pillTransitionName';
import { type ForYouTask, useForYouTasks } from '@/spaces/chat/useForYouTasks';
import { useOnboardingPillarsOverride } from '@/spaces/onboarding/onboardingTransitionContext';
import {
	SLOT_TO_PILL_LABEL,
	type SlotId,
} from '@/spaces/onboarding/screens/strategySummary';
import { cn } from '@/utils/cn';
import { trackEvent } from '@/utils/fullstory';

// Sentinel label for the "For you" pill. It is not a real PillSpec, so it can
// never collide with an actual pill label (the  prefix guarantees it).
const FOR_YOU_LABEL = '\u0000for-you';

type HintInputs = {
	onboardingDone: boolean;
	threadsLoaded: boolean;
	hasZeroThreads: boolean;
	isChatLoading: boolean;
	hasInteracted: boolean;
	expanded: boolean;
	sent: boolean;
	entryReady: boolean;
};

// eslint-disable-next-line react-refresh/only-export-components
export const deriveShowFirstPillHint = (i: HintInputs): boolean =>
	i.onboardingDone &&
	i.threadsLoaded &&
	i.hasZeroThreads &&
	!i.isChatLoading &&
	!i.hasInteracted &&
	!i.expanded &&
	!i.sent &&
	i.entryReady;

const selectPillsForUser = (pillars: SlotId[] | undefined): PillSpec[] => {
	if (!pillars || pillars.length === 0) return PILLS;
	const ordered = pillars.includes('igContent')
		? ['igContent' as SlotId, ...pillars.filter((s) => s !== 'igContent')]
		: pillars;
	const selected = ordered
		.map((slot) => PILLS.find((p) => p.label === SLOT_TO_PILL_LABEL[slot]))
		.filter((p): p is PillSpec => p != null);
	return selected.length > 0 ? selected : PILLS;
};

const getTimeOfDay = (date: Date = new Date()): string => {
	const hour = date.getHours();
	if (hour < 12) return 'Morning';
	if (hour < 18) return 'Afternoon';
	return 'Evening';
};

export const EmptyStateGreeting = () => {
	const { businessName } = useBusinessName();
	const timeOfDay = getTimeOfDay();
	const text = businessName ? `${timeOfDay}, ${businessName}` : timeOfDay;

	return (
		<div className="flex flex-col items-center gap-2 text-center">
			<Text
				styles={style({
					font: 'heading',
					fontSize: 'heading-xl',
					fontWeight: 'extra-bold',
					color: 'title',
				})}
			>
				{text}
			</Text>
			<Text
				styles={style({
					font: 'body',
					fontSize: 'body-lg',
					color: 'body',
				})}
			>
				I&apos;m your AI marketing agent. Ask me anything.
			</Text>
		</div>
	);
};

type EmptyStatePillsProps = {
	threadsLoaded?: boolean;
	hasZeroThreads?: boolean;
	isChatLoading?: boolean;
};

export const EmptyStatePills = ({
	threadsLoaded = false,
	hasZeroThreads = false,
	isChatLoading = false,
}: EmptyStatePillsProps = {}) => {
	const bridgeApi = useBridgeApi();
	const [sent, setSent] = useState(false);
	const [openLabel, setOpenLabel] = useState<string | null>(null);
	const [hasInteracted, setHasInteracted] = useState(false);
	const [entryReady, setEntryReady] = useState(false);
	const rowRef = useRef<HTMLDivElement>(null);
	const [rect, setRect] = useState<{
		left: number;
		top: number;
		width: number;
	} | null>(null);

	const pillarsOverride = useOnboardingPillarsOverride();
	const userPillars =
		pillarsOverride ?? bridgeApi.user_state?.onboarding_status?.pillars;
	const orderedPills = selectPillsForUser(userPillars);

	const tasks = useForYouTasks();

	// For You pill visibility: only present when it has >= 1 item. A dedicated
	// useExitAnimation keeps it mounted through the ~250ms collapse on exit.
	const hasForYou = tasks.length > 0;
	const forYou = useExitAnimation(hasForYou);
	// Freeze the badge count through the exit window so it doesn't blank when
	// tasks drops to 0 mid-collapse (ref-write-during-render mirrors renderedViewRef).
	const lastForYouCountRef = useRef(0);
	if (hasForYou) lastForYouCountRef.current = tasks.length;
	const forYouCount = hasForYou ? tasks.length : lastForYouCountRef.current;
	// Initialized to hasForYou so a pill already present on mount lands fully
	// shown without an entrance animation; only genuine 0->1 / 1->0 animate.
	const [pillEntered, setPillEntered] = useState(hasForYou);
	useEffect(() => {
		if (!forYou.shouldRender || forYou.isClosing) {
			setPillEntered(false);
			return;
		}
		const id = requestAnimationFrame(() => setPillEntered(true));
		return () => cancelAnimationFrame(id);
	}, [forYou.shouldRender, forYou.isClosing]);
	const forYouVisible = forYou.shouldRender && pillEntered && !forYou.isClosing;
	// overflow-hidden is needed to clip the squeeze WHILE animating, but it also
	// clips the pill's drop shadow. Once the shift-in settles, switch to
	// overflow-visible so the pill's shadow renders identically to its siblings.
	const [forYouSettled, setForYouSettled] = useState(hasForYou);
	useEffect(() => {
		if (!forYouVisible) setForYouSettled(false);
	}, [forYouVisible]);

	const currentChatId = bridgeApi.user_state?.current_chat_id ?? null;
	useEffect(() => {
		setSent(false);
		setOpenLabel(null);
		setHasInteracted(false);
	}, [currentChatId]);

	useEffect(() => {
		const id = requestAnimationFrame(() => {
			requestAnimationFrame(() => setEntryReady(true));
		});
		return () => cancelAnimationFrame(id);
	}, []);

	useRegisterUserInputStartHandler(() => setHasInteracted(true));

	const expanded = orderedPills.find((p) => p.label === openLabel) ?? null;
	const isForYouOpen = openLabel === FOR_YOU_LABEL;
	const { shouldRender, isClosing } = useExitAnimation(
		expanded !== null || isForYouOpen,
	);

	// Sticky "currently-rendered popover view" so the exit fade survives the
	// close window (openLabel flips to null before useExitAnimation finishes).
	// A live-open state always wins; during close we fall back to the last view.
	// This covers For You too, whose sentinel never appears in orderedPills and
	// so has no PillSpec to mirror.
	const renderedViewRef = useRef<'for-you' | PillSpec | null>(null);
	if (isForYouOpen) renderedViewRef.current = 'for-you';
	else if (expanded) renderedViewRef.current = expanded;
	const renderedView: 'for-you' | PillSpec | null = isForYouOpen
		? 'for-you'
		: (expanded ?? renderedViewRef.current);
	const renderedIsForYou = renderedView === 'for-you';
	const renderedExpanded: PillSpec | null = renderedIsForYou
		? null
		: renderedView;

	const onboardingDone =
		bridgeApi.user_state?.onboarding_status?.phase === 'done';
	const showFirstPillHint = deriveShowFirstPillHint({
		onboardingDone,
		threadsLoaded,
		hasZeroThreads,
		isChatLoading,
		hasInteracted,
		expanded: expanded !== null,
		sent,
		entryReady,
	});

	const [entered, setEntered] = useState(false);
	useEffect(() => {
		if (!shouldRender || isClosing) {
			setEntered(false);
			return;
		}
		const id = requestAnimationFrame(() => setEntered(true));
		return () => cancelAnimationFrame(id);
	}, [shouldRender, isClosing]);

	useLayoutEffect(() => {
		if (!shouldRender || !rowRef.current) {
			setRect(null);
			return;
		}
		const update = () => {
			const r = rowRef.current?.getBoundingClientRect();
			if (r) setRect({ left: r.left, top: r.top, width: r.width });
		};
		update();
		window.addEventListener('resize', update);
		window.addEventListener('scroll', update, true);
		return () => {
			window.removeEventListener('resize', update);
			window.removeEventListener('scroll', update, true);
		};
	}, [shouldRender]);

	useEffect(() => {
		if (!expanded && !isForYouOpen) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpenLabel(null);
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [expanded, isForYouOpen]);

	const handleSelect = (pill: PillSpec, option: PillOption) => {
		if (sent || isChatLoading) return;
		setSent(true);
		setOpenLabel(null);
		trackEvent('Chat Empty State Option Selected', {
			pill: pill.label,
			option: option.label,
		});
		bridgeApi.sendAction({
			type: 'do_add_user_message_and_force_response',
			content: option.message,
		});
	};

	const markInteracted = () => setHasInteracted(true);

	// For You bypasses the prompt-option send lock entirely.
	const onActivateForYouTask = (task: ForYouTask) => {
		task.onActivate();
		setOpenLabel(null);
	};
	const onRemoveForYouTask = (task: ForYouTask) => {
		// The For You list is derived from unread IG-reply review chats, so
		// removing an item just marks its chat read — the same condition that
		// makes it disappear once the user opens it.
		bridgeApi.sendAction({
			type: 'do_mark_chat_read',
			chat_id: task.id,
		});
	};

	return (
		<>
			<div
				ref={rowRef}
				onMouseEnter={markInteracted}
				onFocus={markInteracted}
				className={cn(
					'flex flex-wrap items-center justify-center gap-2',
					'transition-opacity duration-200 ease-out',
					entered && !isClosing
						? 'pointer-events-none opacity-0'
						: 'opacity-100',
				)}
				aria-hidden={entered && !isClosing}
			>
				{forYou.shouldRender && (
					// Collapsing wrapper: grid track 1fr<->0fr animates width; the
					// negative margin cancels the row's gap so neighbors slide flush.
					// forYouVisible drives both. See the plan's animation state machine.
					<div
						className={cn(
							'grid',
							forYouSettled ? 'overflow-visible' : 'overflow-hidden',
							`transition-[grid-template-columns,opacity,margin-right]
							duration-200 ease-out`,
							'motion-reduce:transition-none',
						)}
						style={{
							gridTemplateColumns: forYouVisible ? '1fr' : '0fr',
							opacity: forYouVisible ? 1 : 0,
							marginRight: forYouVisible ? 0 : '-0.5rem',
						}}
						onTransitionEnd={(e) => {
							if (e.propertyName === 'opacity' && forYouVisible) {
								setForYouSettled(true);
							}
						}}
						aria-hidden={!forYouVisible}
					>
						<div
							className={cn(
								'min-w-0 whitespace-nowrap',
								!forYouVisible && 'pointer-events-none',
							)}
						>
							<PillChip
								key={FOR_YOU_LABEL}
								pill={{ label: 'For You', Icon: AISparklesIcon, options: [] }}
								count={forYouCount || undefined}
								onClick={() => setOpenLabel(FOR_YOU_LABEL)}
								viewTransitionName={pillTransitionName('For You')}
							/>
						</div>
					</div>
				)}
				{orderedPills.map((pill, index) => (
					<PillChip
						key={pill.label}
						pill={pill}
						isDisabled={sent || isChatLoading}
						onClick={() => setOpenLabel(pill.label)}
						viewTransitionName={pillTransitionName(pill.label)}
						isPulsating={showFirstPillHint && index === 0}
					/>
				))}
			</div>

			{(() => {
				if (!shouldRender || !rect) return null;
				if (!renderedIsForYou && !renderedExpanded) return null;
				const ExpandedIcon = renderedExpanded?.Icon;
				const headerLabel = renderedIsForYou
					? 'For You'
					: renderedExpanded!.label;
				return createPortal(
					<>
						<div
							onClick={() => setOpenLabel(null)}
							style={{ position: 'fixed', inset: 0, zIndex: 49 }}
							className={cn(
								'transition-opacity duration-200 ease-out',
								entered && !isClosing ? 'opacity-100' : 'opacity-0',
							)}
						/>
						<div
							style={{
								position: 'fixed',
								left: rect.left,
								top: rect.top,
								width: rect.width,
								zIndex: 50,
							}}
							className={cn(
								'flex flex-col overflow-hidden rounded-[18px] bg-white',
								'shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_1px_3px_0_rgba(0,0,0,0.04),0_8px_24px_0_rgba(0,0,0,0.08)]',
								'transition-opacity duration-200 ease-out',
								entered && !isClosing ? 'opacity-100' : 'opacity-0',
							)}
						>
							<div className="flex items-center justify-between px-5 pt-3 pb-2">
								<div className="flex items-center gap-2 text-(--text-muted)">
									{renderedIsForYou ? (
										<AISparklesIcon className="h-4 w-4" />
									) : (
										ExpandedIcon && (
											<ExpandedIcon
												styles={iconStyle({ size: 'XS', color: 'gray' })}
											/>
										)
									)}
									<span className="text-xs font-normal">{headerLabel}</span>
								</div>
								<RACButton
									onPress={() => setOpenLabel(null)}
									aria-label="Close"
									className="-mr-1 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full outline-none hover:bg-black/5"
								>
									<Close styles={iconStyle({ size: 'XS', color: 'gray' })} />
								</RACButton>
							</div>
							{renderedIsForYou ? (
								<ForYouTaskList
									tasks={tasks}
									onActivate={onActivateForYouTask}
									onRemove={onRemoveForYouTask}
								/>
							) : (
								renderedExpanded!.options.map((option, i) => (
									<RACButton
										key={i}
										isDisabled={sent}
										onPress={() => handleSelect(renderedExpanded!, option)}
										className={cn(
											'flex w-full items-center justify-start px-5 py-3 text-left',
											'text-sm leading-snug text-(--text-primary)',
											'cursor-pointer bg-transparent outline-none',
											i > 0 && 'border-t border-black/[0.06]',
											'hover:bg-black/[0.025]',
											'disabled:cursor-not-allowed disabled:opacity-50',
										)}
									>
										{option.label}
									</RACButton>
								))
							)}
						</div>
					</>,
					document.body,
				);
			})()}
		</>
	);
};
